<script setup>
import { ref, computed, onMounted } from 'vue'
import { debounce } from 'quasar'
import { getI18n, getLocalizedEnumItems } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNotify } from '@/composables/notify'
import { useClone, useIsUpdated } from '@/composables/object'
import { useDetectDevice } from '@/composables/device'
import { usePdfPreview } from '@/composables/pdf-preview'
import { useReportColumnTemplates } from '@/composables/report-column-template'
import { pickPrintTemplateSettings } from '@/composables/print-template'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import PdfPreviewPanel from '@/components/shared/PdfPreviewPanel.vue'
import PdfPreviewPage from '@/components/shared/PdfPreviewPage.vue'
import PrintTemplateStrip from '@/components/accounting/PrintTemplateStrip.vue'
import PrintContentEditor from '@/components/accounting/PrintContentEditor.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['cancel', 'update', 'delete', 'duplicate', 'set-default'])

const STEPS = Object.freeze(['general', 'columns', 'layout'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()
const { templatesOf, isDefault, defaultIdFor, defaultTemplateFor, isConfidential, documentOptions } = useReportColumnTemplates()
const { reportOrientationEnum, reportGroupByEnum, reportDurationFormatEnum, printDocumentTypeEnum, printSettingTypeEnum, reportColumnTemplateDefaultFields, budgetDocumentGroupRowsByLabels } = accountingStore
const printDocumentTypes = Object.freeze({
  [printSettingTypeEnum.QUOTE]: printDocumentTypeEnum.QUOTE,
  [printSettingTypeEnum.INVOICE]: printDocumentTypeEnum.INVOICE,
})
const defaultPrintTemplates = Object.freeze({
  [printDocumentTypeEnum.QUOTE]: webSiteStore.getDefaultQuotePrintTemplate,
  [printDocumentTypeEnum.INVOICE]: webSiteStore.getDefaultInvoicePrintTemplate,
  [printDocumentTypeEnum.REPORT]: webSiteStore.getDefaultReportPrintTemplate,
})

const isNew = !props.modelValue.id
const isCurrentDefault = !isNew && isDefault(props.modelValue)

const editItem = ref({
  name: props.modelValue.name || props.modelValue.label || '',
  document_type: props.modelValue.document_type,
  is_available: props.modelValue.is_available ?? true,
  is_default: isCurrentDefault,
  print_template: props.modelValue.print_template ?? null,
  config: useClone(props.modelValue.config ?? defaultTemplateFor(props.modelValue.document_type).config),
})
const { isUpdated, isUpdating } = useIsUpdated(editItem)
const startTemplateId = ref(isNew ? defaultTemplateFor(editItem.value.document_type)?.id : null)

const step = ref(STEPS[0])
const moveStep = (offset) => { step.value = STEPS[STEPS.indexOf(step.value) + offset] }
const stepDone = (name) => !isNew && name !== step.value

const enumOptions = (enumObject, labelPrefix) => getLocalizedEnumItems(enumObject, labelPrefix).map(item => ({ label: item.name, value: item.id }))
const orientationOptions = enumOptions(reportOrientationEnum, 'common.report.orientations')
const groupByOptions = enumOptions(reportGroupByEnum, 'common.report.groupBy')
const quoteGroupByOptions = Object.entries(budgetDocumentGroupRowsByLabels).map(([value, label]) => ({ label: i18n.t(label), value: Number(value) }))
const reportTypeOptions = accountingStore.getLocalizedPrintSettingTypes().map(type => ({ label: type.name, value: type.id }))
const startTemplateOptions = computed(() => templatesOf(editItem.value.document_type).map(template => ({ label: template.label, value: template.id })))
const isNameValid = computed(() => !!editItem.value.name.trim())
const confidential = computed(() => isConfidential(editItem.value.config))
const isReport = computed(() => accountingStore.isReportSettingType(editItem.value.document_type))
const printDocumentType = computed(() => (isReport.value ? printDocumentTypeEnum.REPORT : printDocumentTypes[editItem.value.document_type] ?? null))
const hasLayoutStep = computed(() => printDocumentType.value !== null)
const title = computed(() => (isNew ? i18n.t('common.report.templateNew') : `${i18n.t('common.edit')} · ${props.modelValue.label}`))

const loadPreviewAsync = async() => {
  if (isReport.value) {
    return (await accountingStore.previewReportAsync({ sample: true, output_config: editItem.value.config, print_template: editItem.value.print_template, language: portalStore.selectedLanguage })).url
  }
  const printTemplateId = editItem.value.print_template ?? defaultPrintTemplates[printDocumentType.value]()
  const printTemplate = accountingStore.getLocalizedDocumentPrintTemplates().find(template => template.id === printTemplateId)
  return accountingStore.previewDocumentPrintTemplateAsync({
    ...(printTemplate && pickPrintTemplateSettings(printTemplate)),
    footer_text: printTemplate?.footer_text,
    document_type: printDocumentType.value,
    output_config: JSON.stringify(editItem.value.config),
  })
}
const { source: previewSource, isLoading: isPreviewLoading, refreshAsync: refreshPreviewAsync } = usePdfPreview(loadPreviewAsync)
const hasPreview = computed(() => printDocumentType.value !== null)
const showPreviewPage = ref(false)
const refreshPreview = debounce(() => { if (hasPreview.value && (!isMobile.value || showPreviewPage.value)) refreshPreviewAsync() }, webSiteStore.getInputDebounce())

const onOpenPreview = () => {
  showPreviewPage.value = true
  refreshPreviewAsync()
}
const onConfigChanged = (config) => {
  editItem.value.config = config
  refreshPreview()
}
const onConfigOptionChanged = (field, value) => onConfigChanged({ ...editItem.value.config, [field]: value })
const onStartTemplateSelected = (templateId) => {
  startTemplateId.value = templateId
  onConfigChanged(useClone(templatesOf(editItem.value.document_type).find(template => template.id === templateId).config))
}
const onDocumentTypeChanged = (reportType) => {
  editItem.value.document_type = reportType
  onStartTemplateSelected(defaultTemplateFor(reportType).id)
}
const onOwnPrintTemplateToggled = (value) => {
  editItem.value.print_template = value ? defaultPrintTemplates[printDocumentType.value]() : null
  refreshPreview()
}
const onPrintTemplateChanged = (templateId) => {
  editItem.value.print_template = templateId
  refreshPreview()
}
const onDefaultToggled = (value) => {
  editItem.value.is_default = value
  if (value) editItem.value.is_available = true
}

const saveAsync = async() => {
  isUpdating.value = true
  try {
    const { is_default: isDefaultTemplate, ...data } = editItem.value
    const { data: saved } = await accountingStore.upsertReportColumnTemplateAsync({ ...(isNew ? {} : { id: props.modelValue.id }), ...data })
    if (isDefaultTemplate && saved.id !== defaultIdFor(saved.document_type)) {
      await accountingStore.updateCompanyDataAsync({ id: accountingStore.companyData.id, [reportColumnTemplateDefaultFields[saved.document_type]]: saved.id })
      await accountingStore.fetchCompanyDataAsync(null, true)
    }
    notifySuccess(i18n.t(isNew ? 'info.createdSuccessfully' : 'info.updatedSuccessfully'))
    emit('update', saved.id)
  } finally {
    isUpdating.value = false
  }
}

onMounted(async() => {
  await accountingStore.ensureDocumentPrintTemplatesAsync()
  if (!isMobile.value && hasPreview.value) refreshPreviewAsync()
})
</script>
<template>
  <q-card class="dialog-card-flex" :style="isMobile ? undefined : 'height: 85vh;'">
    <navigate-back :text="title" @cancel="step === STEPS[0] ? emit('cancel') : moveStep(-1)">
      <template v-if="isMobile" #right>
        <div class="row items-center no-wrap">
          <q-btn
            v-if="hasPreview"
            flat
            round
            icon="mdi-eye-outline"
            @click="onOpenPreview"
          />
          <q-btn
            v-if="!isNew"
            flat
            round
            icon="mdi-dots-vertical"
          >
            <q-menu anchor="bottom right" self="top right">
              <q-list style="min-width: 12.5rem">
                <q-item v-close-popup clickable @click="emit('duplicate')">
                  <q-item-section avatar><q-icon name="mdi-content-copy" /></q-item-section>
                  <q-item-section>{{ i18n.t('common.duplicate') }}</q-item-section>
                </q-item>
                <q-item
                  v-close-popup
                  clickable
                  :disable="isCurrentDefault"
                  @click="emit('set-default')"
                >
                  <q-item-section avatar><q-icon name="mdi-star-outline" /></q-item-section>
                  <q-item-section>{{ i18n.t('common.setAsDefault') }}</q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </div>
      </template>
    </navigate-back>
    <q-card-section class="col q-pa-none row report-template-edit">
      <div class="scroll report-template-edit-form" :class="[isMobile || !hasPreview ? 'col-12' : 'col-5', { 'dialog-card-content': !isNew }]">
        <q-stepper
          v-model="step"
          vertical
          flat
          animated
          class="q-py-none"
        >
          <q-step
            :name="STEPS[0]"
            :title="i18n.t('common.general')"
            :caption="editItem.name"
            icon="mdi-file-document-edit-outline"
            :done="stepDone(STEPS[0])"
            :header-nav="!isNew"
          >
            <div class="row q-col-gutter-sm">
              <q-input
                v-model="editItem.name"
                :class="isMobile ? 'col-12' : 'col-8'"
                :label="i18n.t('common.name')"
                :rules="[() => isNameValid || i18n.t('common.required')]"
              />
              <q-select
                :model-value="editItem.document_type"
                :class="isMobile ? 'col-12' : 'col-4'"
                :options="reportTypeOptions"
                :label="i18n.t('common.report.steps.documentType')"
                :readonly="!isNew"
                emit-value
                map-options
                @update:model-value="onDocumentTypeChanged"
              />
              <form-section-title :label="i18n.t('common.report.templateUsage')" />
              <q-toggle
                v-model="editItem.is_available"
                :class="isMobile ? 'col-12' : 'col-6'"
                :disable="editItem.is_default"
                :label="i18n.t('common.report.templateAvailable')"
              />
              <q-toggle
                :model-value="editItem.is_default"
                :class="isMobile ? 'col-12' : 'col-6'"
                :disable="isCurrentDefault"
                :label="i18n.t('common.report.templateIsDefault')"
                @update:model-value="onDefaultToggled"
              />
            </div>
            <info-box
              v-if="modelValue.report_count"
              class="q-mt-md"
              :message="i18n.t('info.reportColumnTemplateUsage', { count: modelValue.report_count })"
            />
            <stepper-navigation
              :continue="() => moveStep(1)"
              :icon-right="isNew ? 'mdi-chevron-right' : undefined"
              :disable="!isNameValid"
            />
          </q-step>

          <q-step
            :name="STEPS[1]"
            :title="i18n.t('common.report.steps.columns')"
            icon="mdi-table-column"
            :done="stepDone(STEPS[1])"
            :header-nav="!isNew"
          >
            <q-select
              v-if="isNew"
              :model-value="startTemplateId"
              :options="startTemplateOptions"
              :label="i18n.t('common.report.templateStartFrom')"
              class="q-mb-sm"
              emit-value
              map-options
              @update:model-value="onStartTemplateSelected"
            />
            <print-content-editor
              :model-value="editItem.config"
              :options="documentOptions(editItem.config)"
              @update:model-value="onConfigChanged"
              @update:option="onConfigOptionChanged"
            >
              <div v-if="editItem.document_type === printSettingTypeEnum.QUOTE" class="q-mt-sm">
                <div class="text-caption text-grey-7 q-mb-xs">{{ i18n.t('common.report.groupRowsBy') }}</div>
                <q-btn-toggle
                  :model-value="editItem.config.group_by"
                  :options="quoteGroupByOptions"
                  no-caps
                  unelevated
                  dense
                  toggle-color="primary"
                  @update:model-value="value => onConfigOptionChanged('group_by', value)"
                />
              </div>
            </print-content-editor>
            <info-box v-if="confidential" class="q-mt-md" :message="i18n.t('common.report.confidentialInfo')" />
            <stepper-navigation
              :back="() => moveStep(-1)"
              :continue="hasLayoutStep ? () => moveStep(1) : undefined"
              :finish="!hasLayoutStep && isNew ? saveAsync : undefined"
              :finish-label="i18n.t('common.report.templateCreate')"
              :icon-right="isNew ? 'mdi-chevron-right' : undefined"
              :disable="!isNameValid"
              :loading="isUpdating"
            />
          </q-step>

          <q-step
            v-if="hasLayoutStep"
            :name="STEPS[2]"
            :title="i18n.t('common.report.steps.layout')"
            icon="mdi-printer-settings"
            :done="stepDone(STEPS[2])"
            :header-nav="!isNew"
          >
            <q-toggle
              :model-value="editItem.print_template !== null"
              :label="i18n.t('common.report.templateOwnPrintTemplate')"
              @update:model-value="onOwnPrintTemplateToggled"
            />
            <print-template-strip
              v-if="editItem.print_template !== null"
              class="q-mt-xs"
              :model-value="editItem.print_template"
              :document-type="printDocumentType"
              @update:model-value="onPrintTemplateChanged"
            />
            <div v-if="isReport" class="row q-col-gutter-sm q-mt-xs">
              <div class="col-12">
                <div class="text-caption text-grey-7 q-mb-xs">{{ i18n.t('common.report.orientation') }}</div>
                <q-btn-toggle
                  :model-value="editItem.config.orientation"
                  :options="orientationOptions"
                  no-caps
                  unelevated
                  dense
                  toggle-color="primary"
                  @update:model-value="value => onConfigOptionChanged('orientation', value)"
                />
              </div>
              <div class="col-12">
                <div class="text-caption text-grey-7 q-mb-xs">{{ i18n.t('common.report.groupRowsBy') }}</div>
                <q-btn-toggle
                  :model-value="editItem.config.group_by"
                  :options="groupByOptions"
                  no-caps
                  unelevated
                  dense
                  toggle-color="primary"
                  @update:model-value="value => onConfigOptionChanged('group_by', value)"
                />
              </div>
              <q-toggle
                :model-value="editItem.config.total_row"
                :class="isMobile ? 'col-12' : 'col-3'"
                :label="i18n.t('common.report.totalRow')"
                @update:model-value="value => onConfigOptionChanged('total_row', value)"
              />
              <q-toggle
                :model-value="editItem.config.footnotes"
                :class="isMobile ? 'col-12' : 'col-3'"
                :label="i18n.t('common.report.footnotes')"
                @update:model-value="value => onConfigOptionChanged('footnotes', value)"
              />
              <q-toggle
                :model-value="editItem.config.duration_format"
                :class="isMobile ? 'col-12' : 'col-3'"
                :true-value="reportDurationFormatEnum.DECIMAL"
                :false-value="reportDurationFormatEnum.HOURS_MINUTES"
                :label="i18n.t('common.report.decimalDurations')"
                @update:model-value="value => onConfigOptionChanged('duration_format', value)"
              />
            </div>
            <stepper-navigation
              :back="() => moveStep(-1)"
              :finish="isNew ? saveAsync : undefined"
              :finish-label="i18n.t('common.report.templateCreate')"
              :disable="!isNameValid"
              :loading="isUpdating"
            />
          </q-step>
        </q-stepper>
      </div>

      <div v-if="!isMobile && hasPreview" class="report-template-edit-preview column q-pa-md q-gutter-y-sm col-7">
        <span class="text-caption text-uppercase text-grey-7">{{ i18n.t('common.printTemplateLivePreview') }} · {{ i18n.t('common.report.sampleData') }}</span>
        <pdf-preview-panel class="col" :source="previewSource" :loading="isPreviewLoading" />
      </div>
    </q-card-section>

    <q-card-actions v-if="!isNew && (!isMobile || isUpdated)" class="align-center dialog-card-bottom-bar">
      <q-btn
        v-if="!isMobile"
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        :disable="isCurrentDefault"
        @click="emit('delete')"
      >
        <q-tooltip v-if="isCurrentDefault">{{ i18n.t('error.validation.reportColumnTemplateDefaultDelete') }}</q-tooltip>
      </q-btn>
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        :disable="!isNameValid"
        @click="saveAsync"
      />
    </q-card-actions>
    <q-dialog v-model="showPreviewPage" maximized>
      <pdf-preview-page
        :title="i18n.t('common.preview')"
        :source="previewSource"
        :loading="isPreviewLoading"
        @cancel="showPreviewPage = false"
      />
    </q-dialog>
  </q-card>
</template>
<style scoped lang="scss">
.report-template-edit {
  min-height: 0;
}

.report-template-edit-form {
  max-height: 100%;
}

.report-template-edit-preview {
  min-height: 30rem;
  background-color: $grey-4;
}
</style>
